/**
 * Enterprise — NEW landing section for the Enterprise tier.
 *
 * For groups/conglomerates running multiple businesses as one. No public
 * pricing — every Enterprise engagement is a direct conversation, so the
 * only CTA is "Talk to sales" → /book-demo. (Internal pricing structure
 * lives in the operator console, never here.)
 *
 * Design: deep-navy section (Direction 3 operator-world palette) to make
 * it feel like a step up from the green SME sections — visually signals
 * "this is the serious tier." Self-contained, no new asset dependencies.
 */
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Building2,
  Layers,
  Globe,
  FileStack,
  Headset,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";
import { Button } from "../ui/button";

const PILLARS = [
  {
    icon: Layers,
    title: "Unlimited businesses, one cockpit",
    body: "Ventures, construction, farms, financial services — each runs as its own business with its own books, rolled up into a single group view for the owner.",
  },
  {
    icon: Globe,
    title: "Multi-currency, country-aware",
    body: "Trade across borders. Display in cedis, naira, or dollars at live rates cached daily — so every group report is auditable and consistent.",
  },
  {
    icon: FileStack,
    title: "Per-business chart of accounts",
    body: "A restaurant's books aren't a hardware store's. Each business gets an industry-appropriate chart of accounts, all consolidating cleanly at the top.",
  },
  {
    icon: ShieldCheck,
    title: "Audit-grade from day one",
    body: "Every financial mutation, every cross-business action, every login — captured in an append-only audit trail an enterprise client can inspect.",
  },
];

// How an Enterprise account comes together — matches the real flow.
const STEPS = [
  { title: "Tell us about your group", body: "Businesses, branches, people and what you need. We reply within one business day." },
  { title: "Get your proposal", body: "A private link with your package, price and terms. Accept it online when you're ready." },
  { title: "We set everything up", body: "Your businesses, branches and team, with your data moved across. Your account manager walks you through it." },
  { title: "Pay and you're live", body: "Pay by bank transfer, mobile money or card and send the receipt from your welcome page. Once we confirm it, everyone can sign in." },
];

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const Enterprise = () => {
  return (
    <section
      id="enterprise"
      className="relative overflow-hidden bg-gradient-to-b from-[#0A2540] to-[#0E2C44] text-white"
    >
      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
        aria-hidden
      />
      {/* Green glow accent */}
      <div
        className="absolute -top-24 left-1/2 -translate-x-1/2 h-64 w-[40rem] rounded-full bg-[#4CAF50]/20 blur-3xl"
        aria-hidden
      />

      <div className="container relative mx-auto px-4 lg:px-16 py-24 flex flex-col gap-12">
        {/* Header */}
        <motion.div
          {...fade}
          transition={{ duration: 0.5 }}
          className="flex flex-col gap-5 items-center text-center"
        >
          <div className="border border-[#4CAF50]/40 text-[#4CAF50] bg-[#4CAF50]/10 rounded-full px-4 py-2 flex items-center gap-2 w-fit">
            <Building2 className="h-4 w-4" />
            <p className="text-sm font-medium tracking-wide">ENTERPRISE</p>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold leading-tight max-w-3xl">
            Built for groups that{" "}
            <span className="text-[#4CAF50]">run on Eaze</span>
          </h2>
          <p className="text-base md:text-xl text-[#A0BBCB] max-w-2xl">
            When one business becomes many, you need more than a POS. Eaze
            Enterprise gives conglomerates real consolidated books,
            country-aware operations, and the kind of audit trail a serious
            client expects — without the six-figure ERP price tag.
          </p>
        </motion.div>

        {/* Pillars */}
        <div className="grid md:grid-cols-2 gap-5">
          {PILLARS.map(({ icon: Icon, title, body }, i) => (
            <motion.div
              key={i}
              {...fade}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="rounded-2xl border border-[#1E4258] bg-[#0E2C44]/60 backdrop-blur-sm p-6 hover:border-[#4CAF50]/40 transition-colors"
            >
              <span className="inline-flex rounded-xl bg-[#4CAF50]/15 p-2.5 text-[#4CAF50]">
                <Icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-lg font-semibold text-white">{title}</h3>
              <p className="mt-2 text-sm text-[#A0BBCB] leading-relaxed">
                {body}
              </p>
            </motion.div>
          ))}
        </div>

        {/* How it works */}
        <motion.ol {...fade} transition={{ duration: 0.5 }} className="grid gap-4 md:grid-cols-4" aria-label="How Enterprise works">
          {STEPS.map((s, i) => (
            <li key={s.title} className="rounded-2xl border border-[#1E4258] bg-[#0A2540]/60 p-5">
              <span className="text-xs font-semibold tracking-wide text-[#4CAF50]">STEP {i + 1}</span>
              <h3 className="mt-2 text-base font-semibold text-white">{s.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-[#A0BBCB]">{s.body}</p>
            </li>
          ))}
        </motion.ol>

        {/* CTA band */}
        <motion.div
          {...fade}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="rounded-2xl border border-[#1E4258] bg-[#0A2540]/80 p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6"
        >
          <div className="flex items-start gap-4">
            <span className="inline-flex rounded-xl bg-[#4CAF50]/15 p-3 text-[#4CAF50] shrink-0">
              <Headset className="h-6 w-6" />
            </span>
            <div>
              <h3 className="text-xl md:text-2xl font-semibold text-white">
                Every group is different. Let's scope yours.
              </h3>
              <p className="mt-1.5 text-sm md:text-base text-[#A0BBCB] max-w-xl">
                Tell us how your group is structured and we'll design an
                Enterprise setup — onboarding, migration and any custom modules
                — around it. Pricing is tailored to your group, in your
                currency.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 flex-col gap-2 max-md:w-full sm:flex-row">
            <Link to="/enterprise" className="max-md:w-full">
              <Button size="lg" className="bg-[#4CAF50] text-white hover:bg-[#43a047] px-8 max-md:w-full">
                Talk to sales
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/book-demo" className="max-md:w-full">
              <Button size="lg" variant="outline" className="border-[#1E4258] bg-transparent px-8 text-white hover:bg-white/10 hover:text-white max-md:w-full">
                Book a demo
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Enterprise;
